import { colors } from "../../../constants/colorConstants.js";
import { getStatusText } from "../../../utils/orderStatus";
import {
  extractPaymentStatus,
  getPaymentStatusText,
} from "../../../utils/paymentStatus";
import {
  extractTransactionType,
  getTransactionTypeText,
} from "../../../utils/transactionType";
import {
  getDisplayOrderNumber,
  getOrderAddress,
  getOrderRecipientName,
} from "../../../utils/sortUserOrders";
import { getOrderPaymentBreakdown } from "../../../utils/orderPaymentBreakdown";
import { formatPrice } from "../../../utils/formatPrice";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const dash = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return escapeHtml(value);
};

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₪${formatPrice(n)}`;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return escapeHtml(value);
  return String(
    d.toLocaleString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
  );
};

const ORDER_KNOWN_KEYS = new Set([
  "id",
  "orderNumber",
  "customerId",
  "CustomerId",
  "name",
  "CustomerName",
  "phone",
  "Phone",
  "email",
  "Email",
  "address",
  "FullAddress",
  "comment",
  "created",
  "updated",
  "tenant",
  "tenantBranch",
  "branch",
  "tenantStatus",
  "tenantBranchStatus",
  "totalSum",
  "finalTotalSum",
  "finalSum",
  "deliveryPrice",
  "couponDiscount",
  "paymentAdjustmentAction",
  "rank",
  "rankSk",
  "payment",
  "PaymentStatus",
  "transactions",
  "orderItems",
  "dateTimeStatus",
  "_itemsLoadError",
]);

const theme = () => {
  const brand = colors.brandReceipt || "#F97316";
  const dark = colors.dark || "#1E2A5A";
  const muted = colors.textMuted || "#3651A3";
  const border = colors.border || "#E2E8F0";
  const bg = colors.bg || "#F8FAFC";
  const white = colors.white || "#FFFFFF";
  return { brand, dark, muted, border, bg, white };
};

const serializeExtraValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") {
    try {
      return escapeHtml(JSON.stringify(value));
    } catch {
      return escapeHtml(String(value));
    }
  }
  return escapeHtml(String(value));
};

const wrapPage = (innerHtml) => {
  const { dark, white } = theme();
  return `
    <div style="direction:rtl;padding:24px;font-family:'Segoe UI',Tahoma,Arial,sans-serif;color:${dark};background:${white};width:100%;box-sizing:border-box;line-height:1.5;">
      ${innerHtml}
    </div>
  `;
};

const kv = (label, value) => {
  const { border, bg } = theme();
  return `
    <tr>
      <td style="padding:6px 8px;border:1px solid ${border};background:${bg};font-weight:700;width:40%;vertical-align:top;">${escapeHtml(label)}</td>
      <td style="padding:6px 8px;border:1px solid ${border};vertical-align:top;word-break:break-word;">${value}</td>
    </tr>
  `;
};

const sectionTitle = (text) => {
  const { brand, border } = theme();
  return `<div style="margin:18px 0 8px;font-size:14px;font-weight:700;color:${brand};border-bottom:1px solid ${border};padding-bottom:4px;">${escapeHtml(text)}</div>`;
};

const metaTable = (rows) => `
  <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:8px;">
    <tbody>${rows}</tbody>
  </table>
`;

const itemsTable = (items, startIndex = 0) => {
  const { brand, border, muted } = theme();
  if (!items?.length) {
    return `<div style="font-size:12px;color:${muted};margin:6px 0;">אין פריטים להזמנה זו</div>`;
  }
  const head = `
    <tr style="background:${brand};">
      <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">#</th>
      <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">שם מוצר</th>
      <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">ברקוד</th>
      <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">הוזמן</th>
      <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">התקבל</th>
      <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">מחיר יח׳</th>
      <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">סה״כ</th>
      <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">הערה</th>
    </tr>
  `;
  const body = items
    .map((item, idx) => {
      const rowNum = startIndex + idx + 1;
      const name = item.product?.name || item.name || "מוצר לא נמצא";
      const gtin =
        item.product?.gtin ||
        item.gtin ||
        (typeof item.productId === "string"
          ? item.productId.split("-")[0]
          : "") ||
        "—";
      const unit =
        item.price ??
        item.unitPrice ??
        item.apsPriceSnapshot?.unitPrice ??
        null;
      const collected =
        item.collected === null || item.collected === undefined
          ? "—"
          : item.collected;
      return `
        <tr>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:11px;">${rowNum}</td>
          <td style="padding:8px 4px;border:1px solid ${border};font-size:12px;font-weight:500;">${escapeHtml(name)}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:10px;color:${muted};">${escapeHtml(gtin)}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:12px;">${escapeHtml(item.quantity ?? "—")}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:12px;">${escapeHtml(collected)}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:12px;">${money(unit)}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:12px;font-weight:600;">${money(item.totalPrice ?? item.finalTotalPrice)}</td>
          <td style="padding:8px 4px;border:1px solid ${border};font-size:11px;">${dash(item.comment || item.Comment)}</td>
        </tr>
      `;
    })
    .join("");
  return `
    <table style="width:100%;border-collapse:collapse;margin-bottom:10px;page-break-inside:avoid;">
      <thead>${head}</thead>
      <tbody>${body}</tbody>
    </table>
  `;
};

const transactionsTable = (transactions, startIndex = 0) => {
  const { brand, border, muted } = theme();
  if (!Array.isArray(transactions) || !transactions.length) {
    return `<div style="font-size:12px;color:${muted};margin:6px 0;">אין עסקאות תשלום מקושרות</div>`;
  }
  const body = transactions
    .map((tx, idx) => {
      const rowNum = startIndex + idx + 1;
      const status = extractPaymentStatus(tx);
      const typeKey = extractTransactionType(tx);
      const digits = tx.last_four_digits ?? tx.lastFourDigits ?? "";
      return `
        <tr>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:11px;">${rowNum}</td>
          <td style="padding:8px 4px;border:1px solid ${border};font-size:10px;">${dash(tx.id)}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:11px;">${dash(getTransactionTypeText(typeKey) || typeKey)}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:11px;">${dash(getPaymentStatusText(status) || status)}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:12px;">${money(tx.sum ?? tx.amount)}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:11px;">${escapeHtml(formatDateTime(tx.created ?? tx.updated))}</td>
          <td style="padding:8px 4px;border:1px solid ${border};text-align:center;font-size:11px;">${digits ? `****${escapeHtml(digits)}` : "—"}</td>
        </tr>
      `;
    })
    .join("");
  return `
    <table style="width:100%;border-collapse:collapse;margin-bottom:10px;page-break-inside:avoid;">
      <thead>
        <tr style="background:${brand};">
          <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">#</th>
          <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">מזהה</th>
          <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">סוג</th>
          <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">סטטוס</th>
          <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">סכום</th>
          <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">תאריך</th>
          <th style="padding:8px 4px;border:1px solid ${border};font-size:11px;">כרטיס</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
    </table>
  `;
};

export const buildOrdersHistoryCoverHtml = ({ orders, meta }) => {
  const { dark, muted, border, bg } = theme();
  const generatedAt = new Date().toLocaleString("he-IL");
  const totalOrdered = orders.reduce(
    (sum, o) => sum + (Number(o.totalSum) || 0),
    0,
  );
  const totalFinal = orders.reduce(
    (sum, o) => sum + (Number(o.finalTotalSum ?? o.finalSum) || 0),
    0,
  );
  const totalItems = orders.reduce(
    (sum, o) => sum + (Array.isArray(o.orderItems) ? o.orderItems.length : 0),
    0,
  );

  const filterHtml = (meta.filterSummary || [])
    .map(
      (line) =>
        `<div style="font-size:13px;margin:4px 0;color:${dark};">• ${escapeHtml(line)}</div>`,
    )
    .join("");

  return wrapPage(`
    <div style="font-size:22px;font-weight:700;margin-bottom:12px;">היסטוריית הזמנות — המזון</div>
    <div style="padding:14px 16px;border:1px solid ${border};border-radius:8px;background:${bg};margin-bottom:16px;">
      <div style="font-size:14px;font-weight:700;margin-bottom:8px;">סינונים שהתבקשו</div>
      ${filterHtml}
    </div>
    <div style="font-size:13px;margin-bottom:6px;">הזמנות: <strong>${orders.length}</strong> · שורות מוצרים: <strong>${totalItems}</strong></div>
    <div style="font-size:13px;margin-bottom:6px;">סה״כ הזמנות: <strong>${money(totalOrdered)}</strong> · סה״כ לוקט: <strong>${money(totalFinal)}</strong></div>
    <div style="font-size:12px;color:${muted};">נוצר ב: ${escapeHtml(generatedAt)}</div>
  `);
};

/** Keep table pages short so a whole table chunk fits on one PDF page (no mid-row cuts). */
const ITEMS_PER_PDF_PAGE = 16;
const TX_PER_PDF_PAGE = 12;
const TX_INLINE_MAX = 8;

export const buildOrderHistoryHtmlChunks = ({
  order,
  index,
  totalOrders,
  displayIndex = index,
  displayTotal = totalOrders,
}) => {
  const { muted, border } = theme();
  const tenantStatus = order.tenantStatus || order.tenantBranchStatus || "";
  const payment = order.payment || {};
  const breakdown = getOrderPaymentBreakdown(order);
  const extras = Object.keys(order)
    .filter((key) => !ORDER_KNOWN_KEYS.has(key))
    .sort()
    .map((key) => kv(key, serializeExtraValue(order[key])))
    .join("");

  const breakdownHtml = breakdown.length
    ? breakdown
        .map(
          (row) =>
            `<div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;border-bottom:1px dashed ${border};"><span>${escapeHtml(row.label)}</span><strong>${money(row.amount)}</strong></div>`,
        )
        .join("")
    : `<div style="font-size:12px;color:${muted};">אין פירוט תשלום נוסף</div>`;

  const isLastOrder = displayIndex === displayTotal - 1;
  const generatedAt = new Date().toLocaleString("he-IL");
  const items = Array.isArray(order.orderItems) ? order.orderItems : [];
  const transactions = Array.isArray(order.transactions)
    ? order.transactions
    : [];

  const orderNumLabel = displayIndex + 1;

  const orderHeader = `
    <div style="font-size:12px;color:${muted};margin-bottom:2px;">הזמנה ${orderNumLabel} מתוך ${displayTotal}</div>
    <div style="font-size:18px;font-weight:700;margin-bottom:4px;">מס׳ ${escapeHtml(String(getDisplayOrderNumber(order)))}</div>
    <div style="font-size:12px;color:${muted};margin-bottom:12px;">${escapeHtml(formatDateTime(order.created))} · ${dash(order.id)}</div>
  `;

  const continuedHeader = `
    <div style="font-size:12px;color:${muted};margin-bottom:2px;">המשך הזמנה ${orderNumLabel} מתוך ${displayTotal}</div>
    <div style="font-size:16px;font-weight:700;margin-bottom:10px;">מס׳ ${escapeHtml(String(getDisplayOrderNumber(order)))}</div>
  `;

  const chunks = [];

  let detailsHtml = `
    ${orderHeader}
    ${sectionTitle("מזהים ותאריכים")}
    ${metaTable(
      [
        kv("מס׳ הזמנה ללקוח", escapeHtml(String(getDisplayOrderNumber(order)))),
        kv("מזהה הזמנה", dash(order.id)),
        kv("מזהה לקוח", dash(order.customerId || order.CustomerId)),
        kv("נוצרה", escapeHtml(formatDateTime(order.created))),
        kv("עודכנה", escapeHtml(formatDateTime(order.updated))),
        kv("תחנה / סניף", dash(order.tenantBranch || order.branch)),
        kv("דירוג מיון", dash(order.rank)),
      ].join(""),
    )}
    ${sectionTitle("לקוח ומשלוח")}
    ${metaTable(
      [
        kv("שם לקוח / מקבל", dash(getOrderRecipientName(order))),
        kv("מייל", dash(order.email || order.Email)),
        kv("טלפון", dash(order.phone || order.Phone)),
        kv("כתובת משלוח", dash(getOrderAddress(order))),
        kv("הערת לקוח", dash(order.comment)),
      ].join(""),
    )}
    ${sectionTitle("סטטוס וסכומים")}
    ${metaTable(
      [
        kv("סטטוס ליקוט", dash(getStatusText(tenantStatus) || tenantStatus)),
        kv(
          "סטטוס תשלום",
          dash(
            getPaymentStatusText(order.PaymentStatus) ||
              order.PaymentStatus ||
              "",
          ),
        ),
        kv("סכום הזמנה", money(order.totalSum)),
        kv("סכום לוקט / סופי", money(order.finalTotalSum ?? order.finalSum)),
        kv("דמי משלוח", money(order.deliveryPrice)),
        kv("הנחת קופון", money(order.couponDiscount)),
        kv("התאמת תשלום", dash(order.paymentAdjustmentAction)),
      ].join(""),
    )}
    ${sectionTitle("אמצעי תשלום בשמירה")}
    ${metaTable(
      [
        kv(
          "4 ספרות כרטיס",
          payment.lastFourDigits || payment.last_four_digits
            ? `****${escapeHtml(payment.lastFourDigits || payment.last_four_digits)}`
            : "—",
        ),
        kv("תוקף", dash(payment.expiryDate || payment.expiry_date)),
        kv(
          "אסמכתא נדרים",
          dash(payment.nedarimTransactionId || payment.nedarim_transaction_id),
        ),
      ].join(""),
    )}
    ${sectionTitle("פירוט סכומי תשלום")}
    ${breakdownHtml}
  `;

  if (transactions.length <= TX_INLINE_MAX) {
    detailsHtml += `
      ${sectionTitle("עסקאות תשלום")}
      ${transactionsTable(transactions, 0)}
    `;
  }

  if (!items.length) {
    detailsHtml += `
      ${sectionTitle("פירוט מוצרים (הוזמן מול התקבל)")}
      ${itemsTable([], 0)}
    `;
  }

  chunks.push(wrapPage(detailsHtml));

  if (transactions.length > TX_INLINE_MAX) {
    for (let i = 0; i < transactions.length; i += TX_PER_PDF_PAGE) {
      const batch = transactions.slice(i, i + TX_PER_PDF_PAGE);
      const from = i + 1;
      const to = i + batch.length;
      chunks.push(
        wrapPage(`
          ${continuedHeader}
          ${sectionTitle(`עסקאות תשלום (${from}–${to} מתוך ${transactions.length})`)}
          ${transactionsTable(batch, i)}
        `),
      );
    }
  }

  for (let i = 0; i < items.length; i += ITEMS_PER_PDF_PAGE) {
    const batch = items.slice(i, i + ITEMS_PER_PDF_PAGE);
    const from = i + 1;
    const to = i + batch.length;
    const title =
      i === 0
        ? `פירוט מוצרים (הוזמן מול התקבל) — ${from}–${to} מתוך ${items.length}`
        : `המשך מוצרים — ${from}–${to} מתוך ${items.length}`;
    chunks.push(
      wrapPage(`
        ${continuedHeader}
        ${sectionTitle(title)}
        ${itemsTable(batch, i)}
      `),
    );
  }

  if (extras) {
    chunks.push(
      wrapPage(`
        ${continuedHeader}
        ${sectionTitle("שדות נוספים")}
        ${metaTable(extras)}
      `),
    );
  }

  if (isLastOrder && chunks.length) {
    const footer = `<div style="margin-top:24px;font-size:11px;color:${muted};text-align:center;">סוף היסטוריית הזמנות · ${displayTotal} הזמנות · ${escapeHtml(generatedAt)}</div>`;
    const last = chunks[chunks.length - 1];
    chunks[chunks.length - 1] = last.replace(/<\/div>\s*$/, `${footer}</div>`);
  }

  return chunks;
};

export const buildOrderHistoryHtml = ({ order, index, totalOrders }) =>
  buildOrderHistoryHtmlChunks({ order, index, totalOrders }).join("");

/** Full document helper (small exports / tests). */
export const OrdersBackupPdfTemplate = ({ orders, meta }) => {
  const cover = buildOrdersHistoryCoverHtml({ orders, meta });
  const blocks = orders
    .map((order, index) =>
      buildOrderHistoryHtml({
        order,
        index,
        totalOrders: orders.length,
      }),
    )
    .join("");
  return `${cover}${blocks}`;
};
